import React from 'react';

export type StatusColorVariant =
  | 'success'
  | 'error'
  | 'warning'
  | 'warning-stripes'
  | 'info'
  | 'info-stripes'
  | 'neutral'
  | 'dark'
  | 'custom';

export interface StatusPillProps {
  status: string;
  colorVariant?: StatusColorVariant;
  customColorClasses?: string;
  textTransform?: (status: string) => string;
  micro?: boolean;
  className?: string;
}

const StatusPill: React.FC<StatusPillProps> = ({
  status,
  colorVariant = 'neutral',
  customColorClasses,
  textTransform,
  micro = false,
  className = '',
}) => {
  const getColorClasses = (variant: StatusColorVariant): string => {
    if (variant === 'custom' && customColorClasses) {
      return customColorClasses;
    }

    switch (variant) {
      case 'success':
        return 'bg-dataops-forest-green text-white';
      case 'error':
        return 'bg-dataops-crayola-red text-white';
      case 'warning':
        return 'bg-dataops-saffron-yellow text-dataops-rich-black';
      case 'warning-stripes':
        return 'animated-stripes-saffron-yellow text-dataops-rich-black';
      case 'info':
        return 'bg-dataops-process-cyan text-white';
      case 'info-stripes':
        return 'animated-stripes-process-blue text-white';
      case 'dark':
        return 'bg-gray-900 text-white';
      case 'neutral':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDisplayText = (status: string): string => {
    if (textTransform) {
      return textTransform(status);
    }

    // Default transformation: capitalize first letter
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getColorClasses(
        colorVariant,
      )} ${className}`}
    >
      {micro ? getDisplayText(status).slice(0, 1) : getDisplayText(status)}
    </span>
  );
};

export default StatusPill;
